import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Plus, Send, MoreVertical, Star, History } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api } from "../api";
import { useVoice } from "../voice/VoiceProvider";
import { MURSHID } from "../constants/testIds";

// Approved palette only
const C = {
  bg: "#0a2e0a", card: "#0f3d0f", mosaic1: "#1a5c1a", mosaic2: "#4a9a4a",
  high1: "#CEF0CE", high2: "#DFF2DF", white: "#ffffff",
};

const SUGGESTIONS = [
  "What is the significance of Laylat al-Qadr?",
  "What breaks wudu?",
  "Dua for anxiety",
];

const hasArabic = (t) => /[\u0600-\u06FF]/.test(t || "");

// Strip ElevenLabs SSML / emotion / module tags from user-visible text.
// Removes: [calm] [warmly] [MODULE: calendar] [pause]  <break/> <ArabicReciter/>
// {{template_vars}} and collapses runs of whitespace. Preserves Arabic
// Unicode (only bracketed tags are stripped, never plain characters).
function cleanText(text) {
  if (!text) return text;
  return String(text)
    .replace(/\[[^\]]*\]/g, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\{\{[^}]*\}\}/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const uid = () => (globalThis.crypto?.randomUUID?.() ||
  `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  }));

// Parse a Murshid assistant reply into arabic / transliteration / english / citation.
// Heuristics only — no LLM. Best-effort based on common patterns Murshid emits.
function parseResponse(text) {
  if (!text) return { arabic: "", transliteration: "", english: "", citation: "" };
  const src = String(text).trim();

  // Extract citation (last occurrence — usually a suffix)
  let citation = "";
  const cite = src.match(/\[?\s*(?:Source|Ref(?:erence)?)\s*[:\-]\s*([^\]\n]+?)\s*\]?\s*$/i)
    || src.match(/\((?:Surah\s+[^)]+|Sahih\s+[^)]+|Sunan\s+[^)]+|Musnad\s+[^)]+|Al[- ][A-Z][^)]+|Quran\s+\d+:\d+[^)]*)\)\s*$/i)
    || src.match(/^(?:Surah\s+[^\n,.]+|Sahih\s+[^\n,.]+|Quran\s+\d+:\d+[^\n,.]*)$/mi);
  if (cite) citation = cite[1] || cite[0];
  const body = src.replace(cite?.[0] || "", "").trim();

  // Split into lines and classify: any line with Arabic chars → arabic block
  const lines = body.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const arabicLines = [];
  const otherLines = [];
  lines.forEach((l) => (hasArabic(l) ? arabicLines : otherLines).push(l));
  const arabic = arabicLines.join("\n");

  // Transliteration heuristic: italicised or explicitly bracketed romanised
  // rendering typically appears on the line directly after arabic. Detect a
  // block that is (a) not Arabic, and (b) contains diacritics or the tokens
  // Allah / Bismillah / Alhamdulillah / Muhammad — mark as transliteration.
  let transliteration = "";
  const english = [];
  const translitTokens = /(Allah|Bismillah|Alhamdulillah|Astaghfirullah|Muhammad|Rahmani|Rahim|SubhanAllah|Insha'?Allah|Masha'?Allah|Ameen|Al[- ][A-Z])/i;
  otherLines.forEach((l) => {
    const italicMatch = l.match(/^_(.+)_$/) || l.match(/^\*(.+)\*$/);
    if (italicMatch) transliteration = (transliteration ? transliteration + " " : "") + italicMatch[1];
    else if (!transliteration && arabic && translitTokens.test(l) && l.length < 300) transliteration = l;
    else english.push(l);
  });
  return { arabic, transliteration, english: english.join("\n\n"), citation };
}

export default function Murshid() {
  const {
    transcript, clearTranscript,
    sendTextMessage, toggleVoice,
    conversationStatus, isSpeaking, state: voiceState,
    setHideBottomBar,
  } = useVoice();

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // unified {id,role,content,timestamp,source}
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [showSessionsPanel, setShowSessionsPanel] = useState(false);
  const scrollRef = useRef(null);
  const savedRefsRef = useRef(new Set()); // dedup content already POSTed to backend

  // Hide global bottom bar while on this page
  useEffect(() => {
    setHideBottomBar?.(true);
    document.title = "Ask Murshid · Dnya";
    return () => setHideBottomBar?.(false);
  }, [setHideBottomBar]);

  // Load sessions on mount + create a fresh session id
  useEffect(() => {
    loadSessions();
    if (!activeSessionId) setActiveSessionId(uid());
  }, []);

  // Auto-scroll on new messages
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  // Sync ElevenLabs live transcript → unified messages (dedup by role+content+~ts)
  useEffect(() => {
    if (!transcript || transcript.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => `${m.role}|${m.content}`));
      const merged = [...prev];
      for (const t of transcript) {
        const key = `${t.role}|${t.content}`;
        if (!seen.has(key)) {
          merged.push({ id: t.id, role: t.role, content: t.content, timestamp: t.timestamp, source: t.source });
          seen.add(key);
        }
      }
      return merged;
    });
  }, [transcript]);

  // Persist newly-arrived AGENT replies to backend (once each)
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || !activeSessionId) return;
    const key = `${activeSessionId}|assistant|${last.content}`;
    if (savedRefsRef.current.has(key)) return;
    savedRefsRef.current.add(key);
    api.post("/murshid/transcript", {
      session_id: activeSessionId,
      content: last.content,
    }).catch(() => { /* silent — backend persistence is best-effort */ });
  }, [messages, activeSessionId]);

  const loadSessions = async () => {
    try {
      const r = await api.get("/murshid/sessions");
      setSessions(r.data.sessions || []);
    } catch { /* silent */ }
  };

  const startNewSession = () => {
    const id = uid();
    setActiveSessionId(id);
    setMessages([]);
    clearTranscript?.();
    savedRefsRef.current.clear();
    setShowSessionsPanel(false);
    savedRefsRef.current.clear();
  };

  const openSession = async (s) => {
    setShowSessionsPanel(false);
    try {
      const r = await api.get(`/murshid/sessions/${s.id}`);
      const msgs = (r.data.messages || []).map((m, i) => ({
        id: `${s.id}-${i}`, role: m.role, content: m.content,
        timestamp: m.timestamp || new Date().toISOString(), source: "history",
      }));
      setActiveSessionId(s.id);
      setMessages(msgs);
      clearTranscript?.();
      savedRefsRef.current.clear();
      msgs.forEach((m) => savedRefsRef.current.add(`${s.id}|${m.role}|${m.content}`));
    } catch { toast.error("Could not load session"); }
  };

  const toggleBookmark = async (id) => {
    try { await api.put(`/murshid/sessions/${id}/bookmark`); loadSessions(); }
    catch { toast.error("Could not update bookmark"); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/murshid/sessions/${id}`);
      if (id === activeSessionId) startNewSession();
      loadSessions();
    } catch { toast.error("Could not delete"); }
  };

  const send = async (text) => {
    const t = (text || "").trim();
    if (!t || sending) return;
    setSending(true);
    setInputValue("");

    // Optimistic user bubble
    const userMsg = {
      id: uid(), role: "user", content: t,
      timestamp: new Date().toISOString(), source: "text",
    };
    setMessages((prev) => [...prev, userMsg]);
    savedRefsRef.current.add(`${activeSessionId}|user|${t}`);

    // 1) Persist to backend (creates session on first turn)
    try {
      const r = await api.post("/murshid/user-message", {
        session_id: activeSessionId,
        content: t,
      });
      if (r.data?.session_id && r.data.session_id !== activeSessionId) {
        setActiveSessionId(r.data.session_id);
      }
      loadSessions();
    } catch { /* silent — we still send to EL below */ }

    // 2) Ship to ElevenLabs (starts a session if needed)
    try {
      await sendTextMessage?.(t);
    } catch (e) {
      console.error(e);
      toast.error("Could not reach the voice agent");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(inputValue); } };

  // ── Connection pill ─────────────────────────────────────────────────
  const conn = useMemo(() => {
    if (conversationStatus === "connected") return { label: "Connected", color: C.mosaic2, pulse: false };
    if (conversationStatus === "connecting" || voiceState === "processing") return { label: "Connecting...", color: C.mosaic2, pulse: true };
    if (conversationStatus === "error") return { label: "Reconnect", color: C.high2, pulse: false };
    return { label: "Tap mic to start", color: C.mosaic2, pulse: false };
  }, [conversationStatus, voiceState]);

  return (
    <div style={rootStyle} data-testid="murshid-page">
      {/* Slide-in Sessions drawer (hidden by default on ALL screen sizes) */}
      {showSessionsPanel && (
        <div onClick={() => setShowSessionsPanel(false)} style={sessionsOverlayStyle} />
      )}
      <aside
        style={{
          ...panelStyle,
          transform: showSessionsPanel ? "translateX(0)" : "translateX(-100%)",
        }}
        data-testid="murshid-sessions-panel"
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, padding: "0 16px", borderBottom: `1px solid ${C.mosaic1}`, marginBottom: 12, marginLeft: -16, marginRight: -16, marginTop: -16 }}>
          <div style={{ fontFamily: "Cinzel, serif", color: "var(--color-accent-1)", fontSize: 15, fontWeight: 500 }}>Sessions</div>
          <button onClick={() => setShowSessionsPanel(false)} aria-label="Close" style={{ background: "transparent", border: 0, color: "var(--color-accent-1)", cursor: "pointer", fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <button data-testid={MURSHID.newSession} onClick={startNewSession} style={newSessionBtn}>
          <Plus size={14} /> New session
        </button>
        <div style={{ marginTop: 14, overflowY: "auto", flex: 1 }}>
          {sessions.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 12, padding: "12px 4px" }}>No past sessions yet</div>
          ) : sessions.map((s) => {
            const active = s.id === activeSessionId;
            return (
              <div key={s.id} onClick={() => openSession(s)} style={{
                position: "relative", padding: "10px 12px", borderRadius: 8, marginBottom: 6, cursor: "pointer",
                background: active ? C.mosaic1 : "var(--bg-primary)", color: "var(--text-primary)",
                borderLeft: active ? `3px solid var(--color-accent-1)` : "3px solid transparent",
                transition: "background 150ms ease",
              }}>
                <div style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: 34 }}>
                  {s.title || "New conversation"}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                  {new Date(s.updated_at || s.created_at || Date.now()).toLocaleDateString()}
                </div>
                {s.is_bookmarked && <Star size={11} fill="currentColor" color="var(--color-accent-1)" style={{ position: "absolute", right: 34, top: 12 }} />}
                <button
                  onClick={(e) => { e.stopPropagation(); const act = window.prompt("Type 'bookmark' or 'delete' for this session:", ""); if (act === "bookmark") toggleBookmark(s.id); else if (act === "delete") deleteSession(s.id); }}
                  aria-label="Session menu"
                  style={{ position: "absolute", right: 6, top: 8, background: "transparent", border: 0, color: "var(--color-accent-1)", cursor: "pointer", padding: 4 }}
                >
                  <MoreVertical size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Chat area — full width at all times */}
      <section style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, background: "var(--bg-primary)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${C.mosaic1}`, background: "var(--bg-secondary)" }}>
          <div style={{ fontFamily: "Cinzel, serif", color: "var(--color-accent-1)", fontSize: 16, fontWeight: 600 }}>Ask Murshid</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div data-testid={MURSHID.realtimeStatus} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-accent-1)", textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4 }}>
              <span className={conn.pulse ? "dnya-status-pulse" : ""} style={{ width: 8, height: 8, borderRadius: 999, background: conn.color, boxShadow: `0 0 6px ${conn.color}` }} />
              {conn.label}
            </div>
            <button data-testid="murshid-sessions-btn" onClick={() => setShowSessionsPanel(true)} style={headerPillBtn}>
              <History size={12} /> Sessions
            </button>
            <button data-testid={MURSHID.newSession + "-header"} onClick={startNewSession} style={headerPillBtn}>
              <Plus size={12} /> New
            </button>
          </div>
        </div>

        {/* Messages / empty state */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {messages.length === 0 ? (
            <div data-testid={MURSHID.emptyState} style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center", padding: 20 }}>
              <Logo size={64} />
              <div style={{ fontFamily: "Cinzel, serif", color: C.high1, fontSize: 20 }}>Assalamu Alaikum</div>
              <div style={{ color: C.mosaic2, fontSize: 14, maxWidth: 420 }}>Ask Murshid anything about Islam</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 560, marginTop: 6 }}>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={MURSHID.suggestionPill}
                    onClick={() => send(s)}
                    style={{ padding: "8px 16px", borderRadius: 20, background: C.card, color: C.high1, border: `1px solid ${C.high1}`, cursor: "pointer", fontSize: 13 }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : messages.map((m) => (
            <Bubble key={m.id} msg={m} />
          ))}
          {isSpeaking && (
            <div data-testid={MURSHID.typingIndicator} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, color: C.high1 }}>
              <div style={avatarStyle}><Logo size={16} /></div>
              <span className="dnya-dot-1" style={dotStyle}>·</span>
              <span className="dnya-dot-2" style={dotStyle}>·</span>
              <span className="dnya-dot-3" style={dotStyle}>·</span>
            </div>
          )}
        </div>

        {/* Inline input area — replaces global bottom bar */}
        <div style={{ minHeight: 72, background: "var(--bg-secondary)", borderTop: `1px solid ${C.mosaic1}`, padding: "12px 16px", display: "flex", gap: 8, alignItems: "center" }}>
          <input
            data-testid="murshid-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask about Islam..."
            style={{
              flex: 1, minWidth: 0, height: 48, borderRadius: 24, padding: "0 20px",
              background: "var(--bg-primary)", color: "var(--text-primary)",
              border: `1px solid var(--border-color)`, outline: "none",
              fontFamily: "Inter, sans-serif", fontSize: 14, boxSizing: "border-box",
            }}
          />
          <button
            data-testid="murshid-send"
            onClick={() => send(inputValue)}
            disabled={sending || !inputValue.trim()}
            aria-label="Send"
            className="murshid-send-button"
            style={{
              width: 44, height: 44, minWidth: 44, minHeight: 44,
              borderRadius: "50%", flexShrink: 0,
              background: `linear-gradient(135deg, ${C.mosaic1}, ${C.mosaic2})`,
              color: C.white, border: 0, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, boxSizing: "border-box",
              opacity: (sending || !inputValue.trim()) ? 0.55 : 1,
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}
          >
            <Send size={18} />
          </button>
          <button
            data-testid="murshid-mic"
            onClick={toggleVoice}
            aria-label="Voice"
            className={`murshid-mic-button ${voiceState === "listening" ? "dnya-mic-listening" : ""}`}
            style={{
              width: 48, height: 48, minWidth: 48, minHeight: 48,
              borderRadius: "50%", flexShrink: 0,
              background: `linear-gradient(135deg, ${C.mosaic1}, ${C.mosaic2})`,
              color: C.white, border: 0, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, boxSizing: "border-box",
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}
          >
            <Logo size={22} />
          </button>
        </div>
      </section>

      <style>{`
        @keyframes dnyaPulse { 0%{opacity:1} 50%{opacity:0.35} 100%{opacity:1} }
        .dnya-status-pulse { animation: dnyaPulse 1.5s ease-in-out infinite; }
        @keyframes dnyaMicRing { 0%{box-shadow:0 0 0 0 rgba(206,240,206,0.55)} 100%{box-shadow:0 0 0 14px rgba(206,240,206,0)} }
        .dnya-mic-listening { animation: dnyaMicRing 1.5s ease-out infinite; }
      `}</style>
    </div>
  );
}

// ── Bubble component ──────────────────────────────────────────────────
function Bubble({ msg }) {
  const isUser = msg.role === "user";
  // Strip SSML / emotion / module tags before display OR parsing.
  const clean = cleanText(msg.content);
  const parsed = !isUser ? parseResponse(clean) : null;
  return (
    <div data-testid={MURSHID.bubble} style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 12 }}>
      {!isUser && <div style={avatarStyle}><Logo size={16} /></div>}
      <div style={{ maxWidth: isUser ? "70%" : "80%", display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
        <div style={{
          background: isUser ? C.mosaic1 : C.card,
          color: isUser ? C.white : C.white,
          border: isUser ? "none" : `1px solid ${C.mosaic1}`,
          borderRadius: isUser ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
          padding: isUser ? "12px 16px" : "14px 16px",
          fontFamily: "Inter, sans-serif", fontSize: 14, lineHeight: 1.5,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {isUser ? clean : (
            <>
              {parsed.arabic && (
                <div style={{
                  fontFamily: "'Noto Naskh Arabic', serif", fontSize: 18, color: C.high1,
                  direction: "rtl", textAlign: "right", lineHeight: 1.8,
                  borderBottom: parsed.transliteration || parsed.english ? `1px solid ${C.mosaic1}` : "none",
                  paddingBottom: parsed.transliteration || parsed.english ? 8 : 0,
                  marginBottom: parsed.transliteration || parsed.english ? 8 : 0,
                }}>{parsed.arabic}</div>
              )}
              {parsed.transliteration && (
                <div style={{
                  fontFamily: "Inter, sans-serif", fontStyle: "italic", fontSize: 13, color: C.high2,
                  direction: "ltr",
                  borderBottom: parsed.english ? `1px solid ${C.mosaic1}` : "none",
                  paddingBottom: parsed.english ? 8 : 0,
                  marginBottom: parsed.english ? 8 : 0,
                }}>{parsed.transliteration}</div>
              )}
              {parsed.english && (
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: C.white, direction: "ltr" }}>{parsed.english}</div>
              )}
              {!parsed.arabic && !parsed.transliteration && !parsed.english && (
                <div>{clean}</div>
              )}
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: 10, color: C.high1 }}>
            {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
          </span>
          {!isUser && parsed?.citation && (
            <span style={{
              padding: "3px 10px", borderRadius: 20, background: "transparent",
              border: `1px solid ${C.mosaic1}`, color: C.mosaic2,
              fontSize: 11, fontStyle: "italic",
            }}>{parsed.citation}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────
const rootStyle = {
  display: "flex", height: "calc(100vh - 56px)", minHeight: 500, marginTop: -20,
  marginLeft: "calc(-1 * clamp(16px, 4vw, 32px))",
  marginRight: "calc(-1 * clamp(16px, 4vw, 32px))",
  color: "var(--text-primary)",
  position: "relative",
};
// Slide-in Sessions drawer — hidden by default on ALL screen sizes.
// Toggled via the Sessions button in the chat header.
const panelStyle = {
  position: "fixed", top: 56, bottom: 0, left: 0,
  width: "min(85vw, 320px)",
  maxWidth: 320,
  background: "var(--bg-secondary)",
  borderRight: "1px solid var(--border-color)",
  padding: 16, display: "flex", flexDirection: "column",
  zIndex: 600,
  transition: "transform 250ms ease-in-out",
  boxShadow: "4px 0 24px rgba(0,0,0,0.4)",
};
const sessionsOverlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
  zIndex: 599,
};
const headerPillBtn = {
  display: "inline-flex", alignItems: "center", gap: 4,
  background: "transparent", color: "var(--color-accent-1)",
  border: "1px solid var(--border-color)",
  borderRadius: 20, padding: "6px 14px", fontSize: 12,
  cursor: "pointer", minHeight: 32,
};
const newSessionBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  width: "100%", padding: "10px 14px", borderRadius: 8,
  background: "var(--color-mosaic-1)", color: "#ffffff", border: `1px solid var(--border-color)`,
  cursor: "pointer", fontSize: 13, fontWeight: 600, minHeight: 40,
};
const avatarStyle = {
  width: 28, height: 28, borderRadius: 999, background: C.mosaic1,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  marginRight: 8, marginTop: 2, flexShrink: 0,
};
const dotStyle = { fontSize: 24, lineHeight: 0.5, display: "inline-block" };
